/**
 * PWA client controls (offline-and-pwa.md §7 install + §10 update; UI/UX Flow 10):
 *
 *  - <UpdateBanner/> subscribes to `useRegisterSW` (vite-plugin-pwa). When a new SW is
 *    installed and WAITING, it shows the §10 "Update available — reload to apply" banner;
 *    "Reload" calls `updateServiceWorker(true)`, which posts `{ type: 'SKIP_WAITING' }`
 *    to the waiting worker (the channel `service-worker.ts` listens on) and reloads.
 *  - <OfflineBanner/> shows while `navigator.onLine` is false, so a failed request offline
 *    reads as "you're offline", not as a server error.
 *  - <InstallButton/> surfaces the §7 "Install ARGUS" affordance once the browser has fired
 *    `beforeinstallprompt`; on iOS Safari (no prompt API) it offers the "Add to Home
 *    Screen" steps in a popover instead of rendering nothing.
 *
 * All render nothing until there is something to offer, so they are safe to mount always.
 */
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Download, Share, WifiOff } from '../ui/icons.ts';
import { useEffect, useState, type JSX } from 'react';

import { useInstallPrompt } from '../../lib/pwa/use-install-prompt.ts';
import { Button } from '../ui/button.tsx';
import { Popover } from '../ui/popover.tsx';

export function UpdateBanner(): JSX.Element | null {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  if (!needRefresh) return null;

  return (
    <div role="status" aria-live="polite" className="argus-banner">
      <span>Update available — reload to apply.</span>
      <Button
        variant="primary"
        size="sm"
        onClick={() => {
          void updateServiceWorker(true);
        }}
      >
        Reload
      </Button>
      <Button
        variant="secondary"
        size="sm"
        aria-label="Dismiss update notice"
        onClick={() => setNeedRefresh(false)}
      >
        Dismiss
      </Button>
    </div>
  );
}

/** True while the browser reports no network. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const up = (): void => setOnline(true);
    const down = (): void => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export function OfflineBanner(): JSX.Element | null {
  const online = useOnline();
  if (online) return null;
  return (
    <div role="status" aria-live="polite" className="argus-banner argus-banner--offline">
      <WifiOff size={16} aria-hidden="true" />
      <span>You’re offline. Uploads and changes will fail until the connection returns.</span>
    </div>
  );
}

/** iOS Safari has no install prompt API; detect it (and that we are not already installed). */
export function isIosSafariBrowser(
  ua: string = navigator.userAgent,
  standalone: boolean = (navigator as { standalone?: boolean }).standalone === true,
): boolean {
  const ios =
    /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  const safari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return ios && safari && !standalone;
}

export function InstallButton(): JSX.Element | null {
  const { canInstall, promptInstall } = useInstallPrompt();
  const [iosHint, setIosHint] = useState(false);
  const ios = typeof navigator !== 'undefined' && isIosSafariBrowser();

  if (canInstall) {
    return (
      <Button
        variant="secondary"
        size="sm"
        icon={<Download size={14} aria-hidden="true" />}
        onClick={() => {
          void promptInstall();
        }}
      >
        Install ARGUS
      </Button>
    );
  }
  if (!ios) return null;
  return (
    <Popover
      open={iosHint}
      onOpenChange={setIosHint}
      align="end"
      aria-label="Install ARGUS on this iPhone or iPad"
      trigger={
        <Button variant="secondary" size="sm" icon={<Download size={14} aria-hidden="true" />}>
          Install ARGUS
        </Button>
      }
    >
      <div className="stack-sm">
        <strong>Add ARGUS to your Home Screen</strong>
        <ol className="argus-steps">
          <li>
            Tap <Share size={14} aria-hidden="true" /> <strong>Share</strong> in Safari’s toolbar.
          </li>
          <li>
            Choose <strong>Add to Home Screen</strong>.
          </li>
          <li>
            Tap <strong>Add</strong>. ARGUS opens full-screen from the icon.
          </li>
        </ol>
      </div>
    </Popover>
  );
}
